import {
  type ActionReducerMapBuilder,
  createAction,
  type PayloadAction,
} from "@reduxjs/toolkit";

import { transformData } from "../../apis/voyageSchedule";
import { convertToCamelCase, stringToCamelCase } from "../../utils/code-utils";

import type { WebSocketMessage } from "../../utils/types/socket";
import type { clientDetails } from "../../utils/types/type-helper";
import type { Draft } from "immer";

interface StateWithArray {
  [key: string]: any[] | null;
}

interface BerthData {
  [key: string]: any[] | null;
}

const createWebSocketActions = (sliceName: string) => ({
  post: createAction<any>(`${sliceName}/POST`),
  put: createAction<any>(`${sliceName}/PUT`),
  delete: createAction<any>(`${sliceName}/DELETE`),
});

const findItemIndex = (
  array: any[] | null,
  payload: WebSocketMessage
): number => {
  if (!array || !payload?.key || !payload?.value) {
    return -1;
  }

  const originalKey = payload.key;
  const camelKey = stringToCamelCase(originalKey);

  return array.findIndex(
    (item) =>
      item?.[originalKey] === payload.value ||
      item?.[camelKey] === payload.value
  );
};

const handlePostOperation = <T extends StateWithArray>(
  state: any,
  action: PayloadAction<WebSocketMessage>,
  arrayKey: keyof T & string
): void => {
  const { payload } = action;
  const record = payload?.record;

  if (!record) return;

  if (arrayKey === "clientDetails") {
    return;
  }

  if (
    arrayKey === "phoneList" ||
    arrayKey === "vesselList" ||
    arrayKey === "gatesList" ||
    arrayKey === "callsBlackList"
  ) {
    const array = state[arrayKey] as Draft<any[]> | null;
    if (array) {
      array.push(record);
    }
    return;
  }

  if (arrayKey === "scheduleList") {
    const berthDatawithjobId = state[arrayKey] as Draft<any[]> | null;
    if (!berthDatawithjobId) return;
    if (!berthDatawithjobId[record.job_id]) return;
    const transformedData = transformData([record]);
    berthDatawithjobId[record.job_id].push(transformedData[0]);
    return;
  }

  if (["bunker", "deliveryList", "onBoardServices"].includes(arrayKey)) {
    const dataListWithJobId = state[arrayKey] as Draft<BerthData> | null;
    const jobId = record?.job_id;
    if (!dataListWithJobId || !jobId || !dataListWithJobId[jobId]) return;
    const convertedRecord = convertToCamelCase(record);
    dataListWithJobId[jobId]?.push(convertedRecord);
    return;
  }
  if (arrayKey === "operations") {
    const dataListWithBerthId = state[arrayKey] as Draft<BerthData> | null;
    const berthId = record?.berth_id;
    if (!dataListWithBerthId || !berthId || !dataListWithBerthId[berthId])
      return;
    const convertedRecord = convertToCamelCase(record);
    dataListWithBerthId[berthId]?.push(convertedRecord);
    return;
  }

  if (arrayKey === "jobListStatic") {
    state.newJobsAvailable = true;
    state.newJobListStaticAvailable = true;
    return;
  }

  // Default case
  const array = state[arrayKey] as Draft<any[]> | null;
  if (array) {
    const convertedRecord = convertToCamelCase(record);
    if (convertedRecord) {
      array.push(convertedRecord);
    }
  }
};

// const handleJobListDataPost = <T extends StateWithArray>(
//   state: Draft<T>,
//   payload: WebSocketMessage,
//   arrayKey: keyof T & string
// ): void => {
//   if (payload?.storeId === "jobnotificationlist") {
//     console.log("Notification List Job Update Received:", payload);

//     const { record, rootState } = payload;
//     if (!record || !rootState) return;

//     const peopleList = rootState.peopleList?.peopleList || [];
//     const companyList = rootState.companyList?.companyList || [];

//     let foundRecord: any = null;
//     let source = "people";

//     foundRecord = peopleList.find(
//       (people: any) => people?.personId === record?.recipient_id
//     );

//     if (!foundRecord) {
//       foundRecord = companyList.find(
//         (company: any) => company?.companyId === record?.recipient_id
//       );
//       source = "company";
//     }

//     if (!foundRecord) return;

//     const updatedRecord = {
//       recipient_id: record?.recipient_id || null,
//       person_id: source === "people" ? foundRecord?.peopleId || null : null,
//       person_name: source === "people" ? foundRecord?.name || null : null,
//       person_email: source === "people" ? foundRecord?.email || null : null,
//       company_email: source === "company" ? foundRecord?.email || null : null,
//       company_name: source === "company" ? foundRecord?.name || null : null,
//       company_id: source === "company" ? foundRecord?.companyId || null : null,
//       modified_dt: record?.modified_dt || null,
//       type: source,
//     };

//     const array = state[arrayKey] as Draft<any[]> | null;
//     if (!array) return;

//     const index = array.findIndex((item) => item?.job_id === record?.job_id);

//     if (index === -1) return;

//     if (!array[index]?.persons) {
//       array[index].persons = [];
//     }
//     array[index]?.persons?.push(updatedRecord);
//   } else {
//     const array = state[arrayKey] as Draft<any[]> | null;
//     if (array && payload?.record) {
//       array.push(payload.record);
//     }
//   }
// };

const handlePutOperation = <T extends StateWithArray>(
  state: Draft<T>,
  action: PayloadAction<WebSocketMessage>,
  arrayKey: keyof T & string
): void => {
  const { payload } = action;
  const record = payload?.record;

  if (!record) return;

  if (["bunker", "deliveryList", "onBoardServices"].includes(arrayKey)) {
    const dataListWithJobId = state[arrayKey] as Draft<BerthData> | null;
    const jobId = record?.job_id;
    if (!dataListWithJobId || !jobId || !dataListWithJobId[jobId]) return;
    const convertedRecord = convertToCamelCase(record);
    const index = findItemIndex(dataListWithJobId[jobId], payload);
    dataListWithJobId[jobId][index] = convertedRecord;
    return;
  }

  if (arrayKey === "operations") {
    if (payload._changed?.includes("berth_id")) {
      const berthData = state[arrayKey] as Draft<BerthData> | null;
      if (!berthData) return;
      Object.keys(berthData).forEach((berthId) => {
        const berthArray = berthData[berthId];
        if (!berthArray) return;

        if (berthId !== record.berth_id) {
          const existingIndex = berthArray.findIndex(
            (item) =>
              item?.[stringToCamelCase(payload.key as string)] === payload.value
          );
          if (existingIndex !== -1) {
            berthArray.splice(existingIndex, 1);
          }
        } else {
          const existingIndex = berthArray.findIndex(
            (item) =>
              item?.[stringToCamelCase(payload.key as string)] === payload.value
          );
          if (existingIndex === -1) {
            const convertedRecord = convertToCamelCase(record);
            if (convertedRecord) {
              berthArray.push(convertedRecord);
            }
          }
        }
      });
      return;
    }
    const dataListWithBerthId = state[arrayKey] as Draft<BerthData> | null;
    const berthId = record?.berth_id;
    if (!dataListWithBerthId || !berthId || !dataListWithBerthId[berthId])
      return;
    const convertedRecord = convertToCamelCase(record);
    const index = findItemIndex(dataListWithBerthId[berthId], payload);
    dataListWithBerthId[berthId][index] = convertedRecord;
    return;
  }

  if (arrayKey === "scheduleList") {
    handleScheduleListUpdate(state, payload, arrayKey);
    return;
  }

  if (
    arrayKey === "phoneList" ||
    arrayKey === "vesselList" ||
    arrayKey === "gatesList" ||
    arrayKey === "callsBlackList"
  ) {
    const array = state[arrayKey] as Draft<any[]> | null;
    if (!array) return;

    const index = findItemIndex(array, payload);
    if (index !== -1) {
      array[index] = record;
    }
    return;
  }

  if (arrayKey === "jobListStatic") {
    handleJobListDataUpdate(state, payload, arrayKey);
    (state as any).newJobsAvailable = true;
    return;
  }

  if (arrayKey === "clientDetails") {
    const clientData = state[arrayKey] as unknown as clientDetails | null;
    if (clientData?.job_id === record?.job_id) {
      state[arrayKey] = record as any;
    }
    return;
  }

  // Default case
  const array = state[arrayKey] as Draft<any[]> | null;
  if (!array) return;

  const index = findItemIndex(array, payload);
  if (index !== -1) {
    const convertedRecord = convertToCamelCase(record);
    if (convertedRecord) {
      array[index] = convertedRecord;
    }
  }
};

const handleScheduleListUpdate = <T extends StateWithArray>(
  state: Draft<T>,
  payload: WebSocketMessage,
  arrayKey: keyof T & string
): void => {
  const berthDatawithjobId = state[arrayKey] as Draft<any[]> | null;
  if (!berthDatawithjobId || !berthDatawithjobId[payload.record?.job_id])
    return;

  const index = findItemIndex(berthDatawithjobId[payload.record?.job_id], {
    ...payload,
    key: "id",
  });
  if (index === -1) return;

  const { record, rootState } = payload;
  if (!record || !rootState) return;

  const location = rootState.location?.locations?.find(
    (loc: any) => loc?.locationId === record.berth_location_id
  );
  const statusData = rootState.pickList?.pickList?.find(
    (status: any) => status?.id === record.status
  );

  const updatedRecord = {
    ...record,
    berth_location: {
      location_id: record.berth_location_id || null,
      name: location?.name || "",
      type: {
        id: location?.typeId || "",
        value: location?.type?.value || "",
      },
      port_calling: {
        port_callings_id: location?.portCallingsId || "",
        name: location?.portCalling?.name || "",
      },
    },
    status_pick_list: {
      value: statusData?.value || "",
    },
  };

  const transformedData = transformData([updatedRecord]);
  if (transformedData?.[0]) {
    berthDatawithjobId[payload.record?.job_id][index] = {
      ...transformedData[0],
      cargoQuantity:
        berthDatawithjobId[payload.record?.job_id][index]?.cargoQuantity ||
        null,
    };
  }
};

const handleJobListDataUpdate = <T extends StateWithArray>(
  state: Draft<T>,
  payload: WebSocketMessage,
  arrayKey: keyof T & string
): void => {
  const array = state[arrayKey] as Draft<any[]> | null;
  if (!array) return;

  const index = findItemIndex(array, payload);
  if (index === -1) return;

  const { record, rootState, _changed: changes } = payload;
  if (!record || !rootState || !changes) return;

  const pickList = rootState.pickList?.pickList || [];
  const billingList = rootState.billingCodeList?.billingCodeList || [];
  const phonesList = rootState.phoneList?.phoneList || [];
  const vesselList = rootState.vesselList?.vesselList || [];
  const peopleList = rootState.peopleList?.peopleList || [];

  const updatedData = changes.reduce(
    (acc: Record<string, any>, field: string) => {
      if (!Object.prototype.hasOwnProperty.call(record, field)) {
        return acc;
      }

      acc[field] = record[field];

      switch (field) {
        case "status_id": {
          const statusData = pickList.find(
            (status: any) => status?.id === record.status_id
          );
          acc.status = statusData?.value || "";
          break;
        }

        case "billing_id": {
          const billingData = billingList.find(
            (code: any) => code?.accountingId === record.billing_id
          );
          acc.billing_reference = billingData?.billingCode || "";
          break;
        }

        case "assigned_phone_id": {
          const phoneData = phonesList.find(
            (phone: any) => phone?.phone_id === record.assigned_phone_id
          );
          acc.assigned_phone_name = phoneData?.name || "";
          acc.assigned_phone = phoneData?.number || "";
          acc.used_phone_name = phoneData?.name || "";
          acc.used_phone_number = phoneData?.number || "";
          break;
        }

        case "phone_used_id": {
          const phoneData = phonesList.find(
            (phone: any) => phone?.phone_id === record.phone_used_id
          );
          acc.used_phone_name = phoneData?.name || "";
          acc.used_phone_number = phoneData?.number || "";
          break;
        }

        case "vessel_id": {
          const vesselData = vesselList.find(
            (vessel: any) => vessel?.vessel_id === record.vessel_id
          );
          acc.vessel = vesselData?.name || "";
          break;
        }

        case "assigned_agent_id": {
          const peopleData = peopleList.find(
            (person: any) => person?.personId === record.assigned_agent_id
          );
          acc.assigned_agent = peopleData?.name || "";
          break;
        }
        default:
          break;
      }

      return acc;
    },
    {} as Record<string, any>
  );

  array[index] = {
    ...array[index],
    ...updatedData,
  };
};

const handleDeleteOperation = <T extends StateWithArray>(
  state: Draft<T>,
  action: PayloadAction<WebSocketMessage>,
  arrayKey: keyof T & string
): void => {
  const { payload } = action;
  const record = payload?.record;

  if (!record) return;

  if (["bunker", "deliveryList", "onBoardServices"].includes(arrayKey)) {
    const dataListWithJobId = state[arrayKey] as Draft<BerthData> | null;
    const jobId = record?.job_id;
    if (!dataListWithJobId || !jobId || !dataListWithJobId[jobId]) return;
    const newKey = stringToCamelCase(payload?.key || "id") as string;
    dataListWithJobId[jobId] = dataListWithJobId[jobId].filter(
      (item) => item?.[newKey] !== payload?.value
    );
    return;
  }

  if (arrayKey === "operations") {
    const dataListWithBerthId = state[arrayKey] as Draft<BerthData> | null;
    const berthId = record?.berth_id;
    if (!dataListWithBerthId || !berthId || !dataListWithBerthId[berthId])
      return;
    const newKey = stringToCamelCase(payload?.key || "id") as string;
    dataListWithBerthId[berthId] = dataListWithBerthId[berthId].filter(
      (item) => item?.[newKey] !== payload?.value
    );
    return;
  }

  if (arrayKey === "scheduleList") {
    const berthDatawithjobId = state[arrayKey] as Draft<any[]> | null;
    if (!berthDatawithjobId || !berthDatawithjobId[payload.record?.job_id])
      return;

    const array = berthDatawithjobId[payload.record?.job_id];
    if (!array) return;

    const originalKey = payload?.key || "id";
    const camelKey = stringToCamelCase(originalKey);

    berthDatawithjobId[payload.record?.job_id] = array.filter(
      (item: any) =>
        item?.[originalKey] !== payload?.value &&
        item?.[camelKey] !== payload?.value
    ) as any;
    return;
  }

  const array = state[arrayKey] as Draft<any[]> | null;
  if (!array) return;

  const originalKey = payload?.key || "";
  const camelKey = stringToCamelCase(originalKey);

  state[arrayKey] = array.filter(
    (item) =>
      item?.[originalKey] !== payload?.value &&
      item?.[camelKey] !== payload?.value
  ) as any;

  if (arrayKey === "jobListStatic") {
    (state as any).newJobsAvailable = true;
  }
};

export const addWebSocketCases = <T extends StateWithArray>(
  builder: ActionReducerMapBuilder<any>,
  sliceName: string,
  arrayKey: keyof T & string
) => {
  const actions = createWebSocketActions(sliceName);

  builder
    .addCase(
      actions.post,
      (state: Draft<T>, action: PayloadAction<WebSocketMessage>) => {
        try {
          handlePostOperation(state, action, arrayKey);
        } catch (error) {
          console.error(`Error in ${sliceName}/POST for ${arrayKey}:`, error);
        }
      }
    )
    .addCase(
      actions.put,
      (state: Draft<T>, action: PayloadAction<WebSocketMessage>) => {
        try {
          handlePutOperation(state, action, arrayKey);
        } catch (error) {
          console.error(`Error in ${sliceName}/PUT for ${arrayKey}:`, error);
        }
      }
    )
    .addCase(
      actions.delete,
      (state: Draft<T>, action: PayloadAction<WebSocketMessage>) => {
        try {
          handleDeleteOperation(state, action, arrayKey);
        } catch (error) {
          console.error(`Error in ${sliceName}/DELETE for ${arrayKey}:`, error);
        }
      }
    );

  return builder;
};
